import {
  IMAGE_LIBRARY_TYPE,
  ImageLibraryItem,
  ImageLibraryParams,
} from "@/shared/imageLibrary/types";
import { isAiImage } from "@/shared/imageLibrary/aiImage";
import { cn } from "@/shared/utils/cn";
import { errorToast } from "@/shared/utils/helpers";
import { KeyboardEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../Button/Button";
import Icon, { IconsVariant } from "../Icon/Icon";
import AiImageBadge from "../ImageLibrary/AiImageBadge";
import ImageWithFallback from "../ImageWithFallback";
import Modal from "../Modal/Modal";
import ImageUpload, {
  SelectedImage,
  hasBlockingIssues,
} from "./UploadImageWithLibrary/ImageUpload";
import {
  DEFAULT_IMAGE_GUIDELINES,
  ImageGuidelines,
} from "./UploadImageWithLibrary/imageGuidelines";
import LibraryImagePicker from "./UploadImageWithLibrary/LibraryImagePicker";
import libraryImageToFile from "./UploadImageWithLibrary/libraryImageToFile";

type ImageSourceTab = "library" | "upload";

const TABS: { key: ImageSourceTab; icon: IconsVariant }[] = [
  { key: "library", icon: "clone" },
  { key: "upload", icon: "download" },
];

const DEFAULT_LIBRARY_PARAMS: ImageLibraryParams = {
  image_type: IMAGE_LIBRARY_TYPE.product,
};

export interface UploadImageWithLibraryModalProps {
  onExit: () => void;
  /**
   * Called with the chosen images; library images are downloaded as files
   * first, except AI images which are passed as their library url so they are
   * linked instead of uploaded again. The modal stays open while this runs and
   * when it throws.
   */
  onSave: (images: (File | string)[]) => Promise<unknown> | void;
  title?: string;
  subtitle?: string;
  /** Shown next to the title. */
  currentImage?: string;
  /** Without the library only the upload part is shown. */
  showLibrary?: boolean;
  libraryParams?: ImageLibraryParams;
  /** Text the library suggestions are based on, e.g. the product name. */
  librarySuggestFor?: string;
  multiple?: boolean;
  /** Only used with multiple. */
  maxFiles?: number;
  guidelines?: ImageGuidelines;
  saveLabel?: string;
}

/**
 * Pick an image from the image library or upload an own image that is
 * checked against the guidelines.
 */
const UploadImageWithLibraryModal = ({
  onExit,
  onSave,
  title,
  subtitle,
  currentImage,
  showLibrary = true,
  libraryParams = DEFAULT_LIBRARY_PARAMS,
  librarySuggestFor,
  multiple = false,
  maxFiles = Infinity,
  guidelines = DEFAULT_IMAGE_GUIDELINES,
  saveLabel,
}: UploadImageWithLibraryModalProps) => {
  const { t } = useTranslation();
  const tabRefs = useRef<Record<ImageSourceTab, HTMLButtonElement | null>>({
    library: null,
    upload: null,
  });
  const [tab, setTab] = useState<ImageSourceTab>("upload");
  const [libraryImages, setLibraryImages] = useState<ImageLibraryItem[]>([]);
  const [uploads, setUploads] = useState<SelectedImage[]>([]);
  const [isPending, setIsPending] = useState(false);

  const limit = multiple ? maxFiles : 1;
  const activeTab: ImageSourceTab = showLibrary ? tab : "upload";
  const uploadHasErrors = hasBlockingIssues(uploads);
  const selectedNames =
    activeTab === "library"
      ? libraryImages.map((image) => image.name)
      : uploadHasErrors
        ? []
        : uploads.map((upload) => upload.file.name);
  const canSave = selectedNames.length > 0;

  const toggleLibraryImage = (image: ImageLibraryItem) => {
    if (!multiple) return setLibraryImages([image]);
    setLibraryImages((current) =>
      current.some((item) => item.item_id === image.item_id)
        ? current.filter((item) => item.item_id !== image.item_id)
        : [...current, image].slice(0, limit)
    );
  };

  const save = async (images: ImageLibraryItem[] = libraryImages) => {
    if (isPending) return;
    setIsPending(true);
    try {
      let files: (File | string)[];
      if (activeTab === "library") {
        if (images.length === 0) return;
        try {
          files = await Promise.all(
            images.map((image) =>
              isAiImage(image.cloudinary_id)
                ? image.url
                : libraryImageToFile(image)
            )
          );
        } catch {
          errorToast("productImage.errors.libraryDownload");
          return;
        }
      } else {
        if (uploads.length === 0 || uploadHasErrors) return;
        files = uploads.map((upload) => upload.file);
      }
      await onSave(files);
      onExit();
    } catch {
      // Errors are shown as toasts by the caller's upload / update mutations.
    } finally {
      setIsPending(false);
    }
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const next: ImageSourceTab = tab === "library" ? "upload" : "library";
    setTab(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <Modal
      show
      onExit={isPending ? undefined : onExit}
      closeOnClickOutside={!isPending}
      fullHeight
    >
      <div className="mb-8 flex items-center gap-5 pr-16">
        {currentImage && (
          <div className="relative shrink-0">
            <ImageWithFallback
              src={currentImage}
              style={{
                width: "5.6rem",
                height: "5.6rem",
                marginTop: 0,
                borderRadius: "0.8rem",
              }}
            />
            {isAiImage(currentImage) && <AiImageBadge />}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-[2.2rem] font-semibold leading-tight text-text">
            {title ?? t("imageUpload.title")}
          </div>
          {subtitle && (
            <div className="truncate text-small text-text-secondary">
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {showLibrary && (
        <div
          role="tablist"
          aria-label={title ?? t("imageUpload.title")}
          className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          {TABS.map(({ key, icon }) => {
            const isActive = tab === key;
            return (
              <button
                key={key}
                ref={(el) => (tabRefs.current[key] = el)}
                type="button"
                role="tab"
                id={`image-source-tab-${key}`}
                aria-selected={isActive}
                aria-controls={`image-source-panel-${key}`}
                tabIndex={isActive ? 0 : -1}
                disabled={isPending}
                onClick={() => setTab(key)}
                onKeyDown={handleTabKeyDown}
                className={cn(
                  "flex cursor-pointer items-center gap-4 rounded-[1rem] border-2 bg-white p-5 text-left transition-all disabled:cursor-not-allowed",
                  "focus-visible:outline-none focus-visible:shadow-[0_0_0_0.3rem_rgba(59,130,246,0.25)]",
                  isActive
                    ? "border-primary bg-[color-mix(in_srgb,var(--primary)_6%,white)]"
                    : "border-border hover:border-primary"
                )}
              >
                <span
                  className={cn(
                    "flex h-[4.4rem] w-[4.4rem] shrink-0 items-center justify-center rounded-full transition-colors",
                    isActive ? "bg-primary" : "bg-light"
                  )}
                >
                  <Icon
                    className={cn(
                      "h-[1.8rem] w-[1.8rem]",
                      isActive ? "fill-white" : "fill-primary"
                    )}
                    variants={icon}
                  />
                </span>
                <span className="min-w-0">
                  <span className="block text-smaller font-semibold text-text">
                    {t(`productImage.tabs.${key}.title`)}
                  </span>
                  <span className="block text-btn-small text-text-secondary">
                    {key === "library"
                      ? t("productImage.tabs.library.description")
                      : t("imageUpload.uploadDescription")}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Both panels stay mounted so switching tabs keeps the search and selection. */}
      {showLibrary && (
        <div
          role="tabpanel"
          id="image-source-panel-library"
          aria-labelledby="image-source-tab-library"
          hidden={activeTab !== "library"}
        >
          <LibraryImagePicker
            params={libraryParams}
            suggestFor={librarySuggestFor}
            selected={libraryImages}
            onSelect={toggleLibraryImage}
            onConfirm={
              multiple
                ? undefined
                : (image) => {
                    setLibraryImages([image]);
                    save([image]);
                  }
            }
            maxSelected={multiple ? limit : Infinity}
            disabled={isPending}
          />
        </div>
      )}
      <div
        role={showLibrary ? "tabpanel" : undefined}
        id="image-source-panel-upload"
        aria-labelledby={showLibrary ? "image-source-tab-upload" : undefined}
        hidden={activeTab !== "upload"}
      >
        <ImageUpload
          guidelines={guidelines}
          onChange={setUploads}
          multiple={multiple}
          maxFiles={limit}
          disabled={isPending}
        />
      </div>

      <div className="sticky -bottom-8 -mx-8 mt-8 flex flex-col-reverse gap-4 border-t border-border bg-white px-8 py-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 truncate text-small text-text-secondary">
          {selectedNames.length === 1 &&
            t("productImage.selected", { name: selectedNames[0] })}
          {selectedNames.length > 1 &&
            t("imageUpload.selectedCount", { count: selectedNames.length })}
        </div>
        <div className="flex shrink-0 justify-end gap-4">
          <Button
            type="button"
            variant="secondary"
            isDisabled={isPending}
            onClick={onExit}
          >
            {t("cancel")}
          </Button>
          <Button
            type="button"
            isDisabled={!canSave}
            isPending={isPending}
            onClick={() => save()}
          >
            {saveLabel ??
              t(multiple ? "imageUpload.saveMultiple" : "productImage.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UploadImageWithLibraryModal;
